import { Separator } from '@/components/ui/separator'
import ProTip from '../ProTip'

export default function QuickStartSection() {
  return (
    <>
      <p className="text-sm text-muted-foreground">
        New to Grackle CRM? This checklist will get you comfortable with the essentials. Work
        through these steps at your own pace — most people are up and running after completing the
        first four.
      </p>

      <section id="qs-profile" aria-labelledby="qs-profile-heading">
        <h3 id="qs-profile-heading" className="font-semibold mb-2">
          1. Set Up Your Profile
        </h3>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>Click your name or avatar in the top-right corner</li>
          <li>
            Select <span className="font-medium">Settings</span>
          </li>
          <li>Review your name, email, and notification preferences</li>
          <li>
            If your team uses Google Calendar, connect it under the{' '}
            <span className="font-medium">Google Integration</span> section — this syncs your
            meetings automatically
          </li>
        </ol>
      </section>
      <Separator />

      <section id="qs-dashboard" aria-labelledby="qs-dashboard-heading">
        <h3 id="qs-dashboard-heading" className="font-semibold mb-2">
          2. Explore Your Dashboard
        </h3>
        <p className="text-sm text-muted-foreground">
          Your Dashboard is the first thing you see after logging in. Take a moment to review the
          pipeline chart, your key metrics, and the recent activity feed. These give you a snapshot
          of where things stand every morning.
        </p>
      </section>
      <Separator />

      <section id="qs-first-records" aria-labelledby="qs-first-records-heading">
        <h3 id="qs-first-records-heading" className="font-semibold mb-2">
          3. Create Your First Records
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          The best way to learn is by doing. Try creating one of each:
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            Go to <span className="font-medium">CRM &rarr; Leads</span> and click{' '}
            <span className="font-medium">New Lead</span> — enter a name, company, and email
          </li>
          <li>
            Go to <span className="font-medium">Activities &rarr; Tasks</span> and click{' '}
            <span className="font-medium">New Task</span> — give yourself a follow-up reminder
          </li>
          <li>
            Go to <span className="font-medium">CRM &rarr; Accounts</span> and click{' '}
            <span className="font-medium">New Account</span> — add a company you are working with
          </li>
        </ol>
        <p className="text-sm text-muted-foreground mt-2">
          Each record you create builds your working database. The more you add, the more useful
          your CRM becomes.
        </p>
      </section>
      <Separator />

      <section id="qs-daily-routine" aria-labelledby="qs-daily-routine-heading">
        <h3 id="qs-daily-routine-heading" className="font-semibold mb-2">
          4. Your Daily Routine
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          Once you are set up, here is a simple daily workflow that keeps everything on track:
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Morning:</span> Check your Dashboard for overnight updates
            and key metrics
          </li>
          <li>
            <span className="font-medium">Review tasks:</span> Go to Activities &rarr; Tasks to see
            what is due today
          </li>
          <li>
            <span className="font-medium">Work your leads:</span> Open CRM &rarr; Leads and start
            calling or emailing
          </li>
          <li>
            <span className="font-medium">Update records:</span> After each call or meeting, log a
            note or update the lead status
          </li>
          <li>
            <span className="font-medium">End of day:</span> Create follow-up tasks for tomorrow so
            nothing falls through the cracks
          </li>
        </ol>

        <ProTip>
          <span className="font-medium">Build the habit early:</span> Spending two minutes logging
          each interaction keeps your pipeline accurate and saves you from scrambling before team
          reviews. Your future self will thank you.
        </ProTip>
      </section>
    </>
  )
}
